import { useEffect, useMemo, useState } from 'react';
import { listProviderBatches } from '../../api/providerApi';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { Pagination } from '../../components/ui/Pagination';
import { KpiCard } from '../../components/ui/KpiCard';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { IconPackage, IconSearch } from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';
import {
  PACKAGE_STATUS_OPTIONS,
  authorityStateLabel,
  batchDisplayId,
  nextActionLabel,
  packageStatusLabel,
} from './providerReviewLabels';

export function ProviderPackagesPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInputValue.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInputValue]);

  useEffect(() => {
    let cancelled = false;

    async function loadPackages() {
      setLoading(true);
      setError('');
      try {
        const response = await listProviderBatches({
          page,
          pageSize,
          search: search || undefined,
          status: statusFilter || undefined,
        });
        if (cancelled) return;
        setPackages(response.items || []);
        setTotal(response.meta?.total || 0);
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load packages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPackages();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, statusFilter]);

  const columns = useMemo(
    () => [
      {
        key: 'batch',
        header: 'Package',
        sortValue: (row) => batchDisplayId(row),
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {batchDisplayId(row)}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.submit_date)}
            </span>
          </div>
        ),
      },
      {
        key: 'program',
        header: 'Program',
        render: (row) =>
          row.program ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 text-[11px] text-eorig-muted">
              {row.program}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'submitter_org',
        header: 'Submitter',
        render: (row) => row.submitter_org || '—',
      },
      {
        key: 'package_status',
        header: 'Status',
        render: (row) => (
          <StatusPill value={row.package_status} label={packageStatusLabel(row.package_status)} />
        ),
      },
      {
        key: 'authority_status',
        header: 'Authority',
        render: (row) => (
          <StatusPill
            value={row.authority_status}
            label={authorityStateLabel(row.authority_status)}
          />
        ),
      },
      {
        key: 'next_action',
        header: 'Next action',
        sortValue: (row) => nextActionLabel(row.next_action),
        render: (row) => (
          <StatusPill value={row.next_action} label={nextActionLabel(row.next_action)} />
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total packages"
          value={total}
          subline="Matching current filters"
          icon={IconPackage}
          accent
          loading={loading}
        />
      </div>

      <section className="space-y-3 rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <FilterTabs
          tabs={PACKAGE_STATUS_OPTIONS}
          value={statusFilter}
          ariaLabel="Filter by status"
          onChange={(value) => {
            setPage(1);
            setStatusFilter(value);
          }}
        />
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-eorig-muted">
            <IconSearch className="h-4 w-4" />
          </span>
          <input
            value={searchInputValue}
            onChange={(event) => setSearchInputValue(event.target.value)}
            placeholder="Search by reference, program, or submitter"
            aria-label="Search packages"
            className="w-full rounded-full border border-eorig-border bg-white py-2 pl-9 pr-3 text-sm text-eorig-ink placeholder:text-eorig-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
          />
        </div>
      </section>

      {error ? <ErrorBanner message={error} /> : null}

      <DataTable
        columns={columns}
        rows={packages}
        rowKey={(row) => row.batch_id}
        rowHref={(row) => `/provider/packages/${row.batch_id}`}
        loading={loading}
        emptyMessage="No packages found for selected filters."
        minWidth={720}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
